import type { ChangelogEntry } from "./types";

/**
 * Novedades visibles para los usuarios del panel web.
 *
 * Cómo se escribe (detalle en docs/novedades-changelog.md):
 * - La entrada la redacta quien hizo el cambio, en el mismo PR, bajo la
 *   próxima versión y con `fecha: null`. Así se ve en homologación como
 *   "en prueba" y se revisa antes de publicarse.
 * - En el commit de release (bump de versión) se confirma el número de
 *   versión y se completa `fecha`. Nunca se publica una entrada sin fecha
 *   a producción.
 * - Se escribe para el usuario final, en español rioplatense y en presente:
 *   qué puede hacer ahora, o qué dejó de fallar. Nada de nombres de
 *   archivos, endpoints ni commits.
 * - La más nueva va primero.
 */
export const CHANGELOG_ENTRIES: ChangelogEntry[] = [
  {
    version: "1.2.1",
    fecha: "2026-09-16",
    resumen: "Corrección de errores.",
    items: [
      {
        tipo: "correccion",
        area: "Ubicaciones",
        titulo: "Mapa para ubicar locaciones",
        detalle:
          "Al crear o editar una locación, el mapa para posicionar el pin se muestra correctamente e incluye el crédito a OpenStreetMap.",
      },
    ],
  },
  {
    version: "1.2.0",
    fecha: "2026-09-09",
    resumen: "Buscadores en todos los selectores, novedades dentro del sistema y varias correcciones en Programación.",
    items: [
      {
        tipo: "nuevo",
        area: "Sistema",
        titulo: "Novedades del sistema",
        detalle:
          "Esta pantalla. Cada vez que se publica una versión nueva, un punto junto a la versión y en el menú avisa que hay cambios para leer.",
      },
      {
        tipo: "mejora",
        area: "General",
        titulo: "Buscador en todos los selectores",
        detalle:
          "Los selectores de cliente, negocio, planta, vehículo, acoplado y personal tienen buscador y muestran la lista completa: ya no se cortan en los primeros 100 registros.",
      },
      {
        tipo: "mejora",
        area: "General",
        titulo: "La búsqueda ignora las tildes",
        detalle: "Buscar \"nunez\" encuentra \"Núñez\" y \"peru\" encuentra \"Perú\" en todos los listados.",
      },
      {
        tipo: "mejora",
        area: "Programación",
        titulo: "El negocio se ve en las tarjetas de la agenda",
        detalle: "Las tarjetas de \"Sin asignar\" y \"Todos\" muestran el negocio o subcliente debajo del cliente, y la búsqueda del panel también lo tiene en cuenta.",
      },
      {
        tipo: "nuevo",
        area: "Gerencia",
        titulo: "Vista gerencial para el celular",
        detalle: "Nueva vista de solo lectura en /gerencia con el pulso del día y los servicios en curso, pensada para consultar desde el teléfono.",
      },
      {
        tipo: "correccion",
        area: "Programación",
        titulo: "Mover un servicio en la grilla ya no borra el acoplado ni el ayudante",
        detalle: "Al arrastrar, estirar o deshacer un bloque, la tripulación se mantiene tal como estaba. Antes quedaba vacía sin aviso.",
      },
      {
        tipo: "correccion",
        area: "Programación",
        titulo: "Programación y Monitor en Vivo volvían vacíos",
        detalle: "Se corrigió un error que dejaba la agenda \"cargando\" varios segundos y luego sin servicios.",
      },
      {
        tipo: "correccion",
        area: "Ubicaciones",
        titulo: "Vuelven las sugerencias de direcciones sobre ruta",
        detalle: "Al cargar una ubicación aparecen otra vez los resultados de OpenStreetMap además de los de Georef, que son los que mejor cubren las plantas industriales.",
      },
      {
        tipo: "correccion",
        area: "General",
        titulo: "Pantallas que aparecían como \"Módulo desactivado\"",
        detalle: "Las funcionalidades habilitadas por configuración se cargan correctamente para todos los roles, no solo para superadmin.",
      },
      {
        tipo: "correccion",
        area: "General",
        titulo: "Menos pantallas en blanco tras una publicación",
        detalle: "Si una pestaña quedó abierta mientras se publicaba una versión nueva, la aplicación se recarga sola en lugar de mostrar un error.",
      },
      {
        tipo: "correccion",
        area: "Servicios",
        titulo: "Servicios dejaba de responder con el traductor del navegador activo",
        detalle: "La pantalla ya no se cierra con el error \"La aplicación falló al renderizar\" cuando Chrome traduce la página.",
      },
    ],
  },
  {
    version: "1.1.4",
    fecha: "2026-07-02",
    items: [
      {
        tipo: "nuevo",
        area: "Personal",
        titulo: "Examen médico en el legajo del chofer",
        detalle: "Se registra si el chofer tiene examen médico y su vencimiento, con la misma alerta de vencimiento que la LINTI.",
      },
      {
        tipo: "mejora",
        area: "Programación",
        titulo: "Un vencimiento de LINTI o examen médico advierte pero no bloquea",
        detalle: "La asignación muestra la advertencia y deja continuar, igual que las certificaciones por planta.",
      },
      {
        tipo: "correccion",
        area: "Programación",
        titulo: "Los choferes dados de baja ya no aparecen en la grilla",
        detalle: "Tampoco se pueden elegir al asignar un servicio. Los que están de licencia siguen visibles, al final de la lista.",
      },
      {
        tipo: "correccion",
        area: "Programación",
        titulo: "La barra de desplazamiento horizontal queda fija al pie",
        detalle: "La grilla se desplaza dentro de la pantalla, sin tener que bajar hasta la última fila para moverse de horario.",
      },
    ],
  },
];
